import { useRuntimeVariables } from 'app-kit'
import { useKestrel } from 'features/kestrelViewer'
import { useRolodexFileLS } from 'features/rolodex'
import { useMemo } from 'react'
import { KVPhoto, RolodexFileRef } from 'types'

import { PhotoManager } from './photoManager'

const PHOTO_FILE_LIMIT = 5000

const isImageFile = (path: string) => {
  const lowerPath = path.toLowerCase()
  return (
    lowerPath.endsWith('.png') ||
    lowerPath.endsWith('.jpg') ||
    lowerPath.endsWith('.jpeg')
  )
}

const extractImageFiles = (fileRefs: readonly RolodexFileRef[] = []) => {
  return fileRefs
    .filter((file: RolodexFileRef) => isImageFile(file.path))
    .map((file) => ({
      ...file,
      filename: file.path.split('/').pop() || '',
    }))
}

type ExtractedImageFile = RolodexFileRef & { filename: string }

type MergedImageFile = ExtractedImageFile & {
  hasThumbnail: boolean
  hasDownsampled: boolean
  hasRaw: boolean
}

const mergeImageFiles = (
  rawFiles: ExtractedImageFile[],
  downsampledFiles: ExtractedImageFile[],
  thumbnailFiles: ExtractedImageFile[]
): MergedImageFile[] => {
  const imagesByFilename = new Map<string, MergedImageFile>()

  for (const image of rawFiles) {
    imagesByFilename.set(image.filename, {
      ...image,
      hasRaw: true,
      hasDownsampled: false,
      hasThumbnail: false,
    })
  }

  for (const image of downsampledFiles) {
    const existingImage = imagesByFilename.get(image.filename)
    if (existingImage) {
      existingImage.hasDownsampled = true
    } else {
      imagesByFilename.set(image.filename, {
        ...image,
        hasRaw: false,
        hasDownsampled: true,
        hasThumbnail: false,
      })
    }
  }

  for (const image of thumbnailFiles) {
    const existingImage = imagesByFilename.get(image.filename)
    if (existingImage) {
      existingImage.hasThumbnail = true
    } else {
      imagesByFilename.set(image.filename, {
        ...image,
        hasRaw: false,
        hasDownsampled: false,
        hasThumbnail: true,
      })
    }
  }

  const images = Array.from(imagesByFilename.values())
  images.sort((a, b) =>
    a.filename.toLowerCase().localeCompare(b.filename.toLowerCase())
  )
  return images
}

const buildRolodexQuery = (
  entryId: string | undefined,
  subPath: 'thumbnails' | 'downsampled' | 'raw'
) =>
  entryId
    ? {
        entry_id: entryId,
        path: `/photos/${subPath}/**`,
        limit: PHOTO_FILE_LIMIT,
      }
    : undefined

export const usePhotoFiles = (
  entryId: string | undefined
): {
  photos: KVPhoto[]
  isLoading: boolean
  error: Error | null
} => {
  const { authorizedAxios } = useKestrel()
  const { ROLODEX_URL } = useRuntimeVariables()

  const {
    fileRefs: thumbnailFileRefs,
    isLoading: thumbnailsLoading,
    error: thumbnailsError,
  } = useRolodexFileLS(buildRolodexQuery(entryId, 'thumbnails'))

  const {
    fileRefs: downsampledFileRefs,
    isLoading: downsampledLoading,
    error: downsampledError,
  } = useRolodexFileLS(buildRolodexQuery(entryId, 'downsampled'))

  const {
    fileRefs: rawFileRefs,
    isLoading: rawLoading,
    error: rawError,
  } = useRolodexFileLS(buildRolodexQuery(entryId, 'raw'))

  const thumbnailImageFiles = useMemo(
    () => extractImageFiles(thumbnailFileRefs),
    [thumbnailFileRefs]
  )

  const downsampledImageFiles = useMemo(
    () => extractImageFiles(downsampledFileRefs),
    [downsampledFileRefs]
  )

  const rawImageFiles = useMemo(
    () => extractImageFiles(rawFileRefs),
    [rawFileRefs]
  )

  const imageFiles = useMemo(
    () =>
      mergeImageFiles(
        rawImageFiles,
        downsampledImageFiles,
        thumbnailImageFiles
      ),
    [rawImageFiles, downsampledImageFiles, thumbnailImageFiles]
  )

  const photoManager = useMemo(() => {
    if (!entryId || !authorizedAxios || !ROLODEX_URL) return null

    return new PhotoManager({
      entryID: entryId,
      axios: authorizedAxios,
      rolodexURL: ROLODEX_URL,
    })
  }, [entryId, authorizedAxios, ROLODEX_URL])

  const photos = useMemo(() => {
    if (!photoManager) return []

    const createFetcher =
      (
        filename: string,
        location: 'thumbnails' | 'downsampled' | 'raw',
        available: boolean
      ) =>
      () => {
        if (!available) {
          return Promise.reject(new Error(`Photo ${location} not available`))
        }
        return photoManager.fetchPhoto(filename, location)
      }

    return imageFiles.map((file) => ({
      entryId: file.entry_id,
      contentType: file.content_type,
      path: file.path,
      filename: file.filename,
      id: `${file.entry_id}_${file.path}`,
      hasThumbnail: file.hasThumbnail,
      hasDownsampled: file.hasDownsampled,
      hasRaw: file.hasRaw,
      getThumbnailUrl: createFetcher(
        file.filename,
        'thumbnails',
        file.hasThumbnail
      ),
      getFullSizeUrl: createFetcher(file.filename, 'raw', file.hasRaw),
      getDownsampledUrl: createFetcher(
        file.filename,
        'downsampled',
        file.hasDownsampled
      ),
    }))
  }, [imageFiles, photoManager])

  const photosLoading = thumbnailsLoading || downsampledLoading || rawLoading
  const error = rawError || downsampledError || thumbnailsError

  return {
    photos,
    isLoading: photosLoading,
    error,
  }
}
